/**
 * harness/searchHistory.js
 * T-Cron-2026-10-24 08:00 早间 (round-1-of-day) — 改进清单 #15「搜索:历史」
 *
 * 背景:改进清单 #15 原文 =「搜索:历史 / 拼音 / 标签云」。
 *   · 拼音首字母 —— 已在 T99 轮落地(vite/src/harness/pinyin.js)。
 *   · 标签云     —— 数据侧已有 tagList,本轮不做(见下方「不做标签云的理由」)。
 *   · 历史       —— 本文件。
 *
 * 为什么需要「历史」:
 *   看板的典型用法是**反复查同一个东西** —— 「查一下《黑天鹅》相关的」、
 *   「再搜昨天那个热点」。但每次都要重新打字。历史把「最近查过什么」
 *   变成一个可点的列表,一次点击回到那个查询。
 *
 * 为什么不直接复用 store.filters.search:
 *   filters.search 是**当前**查询(受控输入框的值),历史是**过去**的查询集合,
 *   两者语义不同 ⇒ 独立存储,独立生命周期。
 *
 * 存储口径:
 *   · localStorage key = `xhs-search-history`(和 favorites 用同一个持久化
 *     通道的模式,但 key 独立,互不干扰);
 *   · 最多留 8 条(FIFO);
 *   · 去重:同一个查询再次提交 ⇒ 移到最前,而不是出现两条;
 *   · **少于 2 个字符的查询不记录** —— 单字查询(如「书」)几乎一定是
 *     用户刚开始打字时的中间态,记录下来会污染历史列表。
 *
 * 提交时机(在调用方 App.jsx 里做,本模块只管存取):
 *   · Enter 键;
 *   · 输入框失焦(blur)—— 用户点别处/点卡片时说明这个查询是认真的。
 *   两者都调用 commitSearch,模块内已去重 ⇒ 重复提交无害。
 *
 * 注意:本文件是 .js(不是 .jsx)⇒ **必须显式 import React**
 * (vite.config.js 的 esbuild.jsxInject 只对 .jsx 生效)。
 */
import React from 'react';

const STORAGE_KEY = 'xhs-search-history';

/** 最多保留多少条历史 */
export const MAX_HISTORY = 8;

/** 少于这个长度的查询不记入历史(大概率是打字中间态) */
const MIN_QUERY_LEN = 2;

/**
 * 从 localStorage 读历史。任何异常(隐私模式 / 配额满 / JSON 损坏)
 * 一律返回空数组 —— 历史是**锦上添花**,读不出来绝不能让整个看板崩掉。
 */
export function loadSearchHistory() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => typeof x === 'string' && x.trim())
      .map((x) => x.trim())
      .slice(0, MAX_HISTORY);
  } catch (_) {
    return [];
  }
}

/**
 * 写历史(带去重 + 截断)。返回真正写下去的那一份(调用方拿它 setState)。
 */
export function saveSearchHistory(list) {
  const clean = [];
  const seen = new Set();
  for (const item of list || []) {
    const q = String(item == null ? '' : item).trim();
    if (!q || seen.has(q)) continue;
    seen.add(q);
    clean.push(q);
    if (clean.length >= MAX_HISTORY) break;
  }
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
    }
  } catch (_) {
    // 写失败(配额满)不影响本次会话内的使用
  }
  return clean;
}

/**
 * 记一次查询:去重 + 移到最前 + 截断。
 * @param {string} q
 * @returns {string[]} 新的历史列表
 */
export function pushSearchHistory(q) {
  const s = String(q == null ? '' : q).trim();
  if (s.length < MIN_QUERY_LEN) return loadSearchHistory();
  const rest = loadSearchHistory().filter((x) => x !== s);
  return saveSearchHistory([s, ...rest]);
}

/** 删掉一条(点 × 按钮) */
export function removeSearchHistory(q) {
  const s = String(q == null ? '' : q).trim();
  return saveSearchHistory(loadSearchHistory().filter((x) => x !== s));
}

/** 清空全部历史 */
export function clearSearchHistory() {
  return saveSearchHistory([]);
}

/**
 * React 侧包装:状态 + 三个操作。
 *
 * 刻意**不做**「跨标签页同步」:storage 事件监听在多标签页场景下
 * 会让历史列表在另一个标签页突然变化,反而让人困惑 —— 一台设备一个人用,
 * 收益远小于复杂度。
 */
export function useSearchHistory() {
  const [history, setHistory] = React.useState(() => loadSearchHistory());

  const commit = React.useCallback((q) => {
    setHistory(pushSearchHistory(q));
  }, []);

  const remove = React.useCallback((q) => {
    setHistory(removeSearchHistory(q));
  }, []);

  const clear = React.useCallback(() => {
    setHistory(clearSearchHistory());
  }, []);

  return { history, commit, remove, clear };
}

const SearchHistory = {
  MAX_HISTORY,
  loadSearchHistory,
  saveSearchHistory,
  pushSearchHistory,
  removeSearchHistory,
  clearSearchHistory,
  useSearchHistory,
};

if (typeof window !== 'undefined') window.SearchHistory = SearchHistory;

export default SearchHistory;
